/**
 * What the campaign screens have already loaded, kept for the length of the browser session, so
 * moving from one page to the next shows it at once instead of fetching it all again.
 *
 *   fetch(key, fetcher, { maxAgeMs, force })
 *     The held value while it is younger than maxAgeMs. Otherwise one request — shared by every
 *     screen that asks for the same key while it is in flight. `force` always asks the server
 *     (a refresh the person pressed, a poll).
 *
 *   peek(key)
 *     Whatever is held, fresh or not, so a screen can draw immediately and refresh behind it.
 *
 *   clear()
 *     Called after anything is written (see isWrite and the apiService interceptor). An answer
 *     that started before the write is still handed to whoever asked for it, but it is never kept:
 *     an old answer must not outlive the change that made it old.
 *
 * Memory only — nothing is written to disk, and a full page reload starts empty.
 */
export function createQueryCache({ now = () => Date.now() } = {}) {
  const entries = new Map();
  const inflight = new Map();
  let generation = 0;

  function peek(key) {
    return entries.get(key) ?? null;
  }

  function fetch(key, fetcher, { maxAgeMs = 0, force = false } = {}) {
    const held = entries.get(key);
    if (!force && held && now() - held.at <= maxAgeMs) return Promise.resolve(held.data);

    const pending = inflight.get(key);
    if (pending && pending.generation === generation) return pending.promise;

    const startedIn = generation;
    const promise = Promise.resolve()
      .then(fetcher)
      .then((data) => {
        if (startedIn === generation) entries.set(key, { data, at: now() });
        return data;
      })
      .finally(() => {
        if (inflight.get(key)?.promise === promise) inflight.delete(key);
      });
    inflight.set(key, { promise, generation: startedIn });
    return promise;
  }

  function clear() {
    generation += 1;
    entries.clear();
    inflight.clear();
  }

  return { peek, fetch, clear };
}

/** The one cache the campaign screens share. */
export const queryCache = createQueryCache();

/** Requests that are sent as writes but only read: previews, prechecks, estimates, sheet parsing, token refresh. */
const READ_ONLY_WRITE = /\/(preview|precheck|estimate|refresh)(\/|\?|$)|\/audience\/import(\/|\?|$)/;

/** Whether a finished request may have changed data a screen is holding. */
export function isWrite(method, url) {
  const m = String(method || 'get').toLowerCase();
  if (m === 'get' || m === 'head' || m === 'options') return false;
  return !READ_ONLY_WRITE.test(String(url || ''));
}
